import { makeAutoObservable } from "mobx";
import { Availability } from "../Availability";
import { ParameterScope } from "../ParameterScope";
import { Quantity } from "../Quantity";
export class ModifierItemInstance {
    constructor(params) {
        this.scope = ParameterScope.Uninitialized;
        this.itemQuantities = params.itemQuantities;
        this.modifierItemType = params.modifierItemType;
        this.maxDuplicatesPerItem = params.maxDuplicatesPerItem;
        this.defaultQuantity = params.defaultQuantity;
        this.isMultipleChoice = params.isMultipleChoice;
        this.editability = params.editability;
        this.modifierTypeAvailability = params.modifierTypeAvailability;
        makeAutoObservable(this);
    }
    setScope(scope) {
        this.scope = scope;
    }
    get typeId() {
        return this.modifierItemType.id;
    }
    get quantity() {
        if (this.isPossiblyAvailable) {
            return this.itemQuantities.quantity(this.typeId);
        }
        return Quantity.Zero;
    }
    get isEditable() {
        return this.editability.isPossiblyEditable && this.isPossiblyAvailable;
    }
    get unitAvailability() {
        return Availability.composite([
            this.modifierTypeAvailability.availability,
            Availability.ofModifierItemType(this.modifierItemType, this.scope),
        ]);
    }
    get isPossiblyAvailable() {
        return this.unitAvailability.isAvailable.isPossible;
    }
    get isDefault() {
        return this.defaultQuantity.value > 0;
    }
    get canIncrement() {
        if (!this.isEditable) {
            return false;
        }
        if (this.maxDuplicatesPerItem !== null &&
            this.quantity.geq(this.maxDuplicatesPerItem)) {
            return false;
        }
        return true;
    }
    increment() {
        if (this.canIncrement) {
            this.itemQuantities.increment(this.typeId);
        }
    }
    get canDecrement() {
        if (!this.isEditable) {
            return false;
        }
        if (this.modifierItemType.irremovable &&
            this.quantity.leq(this.defaultQuantity)) {
            return false;
        }
        return this.quantity.value > 0;
    }
    decrement() {
        if (this.canDecrement) {
            this.itemQuantities.decrement(this.typeId);
        }
    }
    get isSelected() {
        return this.quantity.value > 0 && this.isPossiblyAvailable;
    }
    setSelected(selected) {
        if (!this.isEditable) {
            throw new Error(`ModifierItemInstance not editable`);
        }
        if (this.modifierItemType.irremovable && this.isDefault && !selected) {
            return;
        }
        this.itemQuantities.setQuantity(this.typeId, selected ? Quantity.One : Quantity.Zero);
    }
    select() {
        if (!this.isEditable) {
            throw new Error(`ModifierItemInstance not editable`);
        }
        this.itemQuantities.clear();
        this.itemQuantities.setQuantity(this.typeId, Quantity.One);
    }
    get isDisabled() {
        if (!this.isPossiblyAvailable) {
            const irremovableIngredient = this.isDefault && this.modifierItemType.irremovable;
            const temporarilyUnavailable = this.unitAvailability.unavailabilityReasons.includes("TemporarilyUnavailable");
            return irremovableIngredient || temporarilyUnavailable;
        }
        return false;
    }
    clone(params) {
        return new ModifierItemInstance({
            itemQuantities: params.itemQuantities,
            modifierItemType: this.modifierItemType,
            maxDuplicatesPerItem: this.maxDuplicatesPerItem,
            defaultQuantity: this.defaultQuantity,
            isMultipleChoice: this.isMultipleChoice,
            editability: params.editability,
            modifierTypeAvailability: params.modifierTypeAvailability,
        });
    }
}
